/**
 * Validation utilities for Secure Architecture VLAN configuration.
 * Extracted from SecureArchitectureHook for testability.
 */

/**
 * Validates FortiGate VLAN configuration before saving.
 * @param {Object} secureCommunicationData - The current secure communication data
 * @param {Array} hasConflicts - Array of VLAN names used as trunk members
 * @returns {string|null} Error message if invalid, null if valid
 */
export const validateFortigateConfiguration = (
  secureCommunicationData,
  hasConflicts
) => {
  const fortigateConfigs =
    secureCommunicationData?.fortigateVlanConfigurations || [];
  const connexiumConfigs =
    secureCommunicationData?.connexiumFortigateVlanConfigurations || [];

  // Check FortiGate Port configurations
  const invalidFortigatePort = fortigateConfigs.find((config) => {
    if (["HYBRID_TRUNK", "TRUNK"].includes(config.memberType)) {
      return !config.hybridMembers || config.hybridMembers.length === 0;
    }
    // Check VLAN_SWITCH with MGMT that is first one
    if (
      config.memberType === "VLAN_SWITCH" &&
      config.accessMembers?.vlan === "MGMT"
    ) {
      const isFirst =
        fortigateConfigs.findIndex(
          (c) =>
            c.memberType === "VLAN_SWITCH" && c.accessMembers?.vlan === "MGMT"
        ) === fortigateConfigs.indexOf(config);
      if (isFirst) {
        return !config.hybridMembers || config.hybridMembers.length === 0;
      }
    }
    return false;
  });

  // Check Connexium configurations
  const invalidConnexiumPort = connexiumConfigs.find((config) => {
    if (["HYBRID_TRUNK", "TRUNK"].includes(config.memberType)) {
      return !config.hybridMembers || config.hybridMembers.length === 0;
    }
    return false;
  });

  if (invalidFortigatePort) {
    return `Please select trunk members for port ${invalidFortigatePort.port} before saving.`;
  }

  if (invalidConnexiumPort) {
    return `Please select trunk members for Connexium port ${invalidConnexiumPort.port} before saving.`;
  }

  // Check for conflicts - any port using a VLAN that's in hasConflicts (trunk members)
  const conflictingPort = fortigateConfigs.find((config) => {
    const accessVlan = config?.accessMembers?.vlan;
    return accessVlan && hasConflicts.includes(accessVlan);
  });

  if (conflictingPort) {
    return `Port ${conflictingPort.port} is using VLAN ${conflictingPort.accessMembers.vlan} which is already assigned as a trunk member. Please resolve the conflict before saving.`;
  }

  // Check Connexium ports - if hasConflicts has values, VLAN-type ports must use VLANs from hasConflicts
  // TRUNK ports are exempt from this validation
  if (hasConflicts && hasConflicts.length > 0) {
    const invalidConnexiumAccessMember = connexiumConfigs.find((config) => {
      if (config.memberType === "TRUNK") return false;
      const accessVlan = config?.accessMembers?.vlan;
      return accessVlan && !hasConflicts.includes(accessVlan);
    });

    if (invalidConnexiumAccessMember) {
      return `Connexium port ${invalidConnexiumAccessMember.port} must use a VLAN from the trunk members list. Please select a valid VLAN before saving.`;
    }
  }

  return null;
};

/**
 * Validates Moxa VLAN configuration before saving.
 * @param {Object} secureCommunicationData - The current secure communication data
 * @returns {string|null} Error message if invalid, null if valid
 */
export const validateMoxaConfiguration = (secureCommunicationData) => {
  const moxaConfigs =
    secureCommunicationData?.moxaVlanConfigurations || [];
  const connexiumConfigs =
    secureCommunicationData?.connexiumVlanConfigurations || [];

  const invalidMoxaPort = moxaConfigs.find((config) => {
    if (["HYBRID_TRUNK"].includes(config.memberType)) {
      return !config.hybridMembers || config.hybridMembers.length === 0;
    }
    return false;
  });

  const invalidConnexiumPort = connexiumConfigs.find((config) => {
    if (["HYBRID_TRUNK"].includes(config.memberType)) {
      return !config.hybridMembers || config.hybridMembers.length === 0;
    }
    return false;
  });

  if (invalidMoxaPort) {
    return `Please select trunk members for port ${invalidMoxaPort.port} before saving.`;
  }

  if (invalidConnexiumPort) {
    return `Please select trunk members for Connexium port ${invalidConnexiumPort.port} before saving.`;
  }

  return null;
};
